import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { IOptionValues, IProduct } from "../../../models/catalogue";
import { IImageURL } from "../../../models/nominals";
import { IViewport } from "../../../models/screen";
import { check } from "../../../models/utils";
import { loadWistiaPlayer } from "../../../utils/wistia";
import {
    baseVariantSelector,
    rootProductSelector,
} from "../../configurator/selectors";
import { useAppSelector } from "../../reducers";
import {
    GalleryBlock as GalleryBlockCodec,
    IAttributePair,
    IGallery,
    IGalleryBlock,
} from "../models";
import { Gallery } from "./Gallery";
import { GalleryPlaceHolder } from "./GalleryPlaceHolder";

interface IGalleryBlockProps {
    galleriesJSON: string;
    defaultThumbnail: IImageURL;
    initialVariantId?: string | null;
}

const decodeGalleryJSON = (json: string): IGalleryBlock => {
    return check(GalleryBlockCodec.decode(JSON.parse(json)));
};

const attributesInclude = (
    attributes: IOptionValues,
    attributeVal: IAttributePair,
): boolean => {
    return !!Object.entries(attributes).find(([c, v]) => {
        return c === attributeVal.code && v === attributeVal.value;
    });
};

const attributesMatch = (
    attributes: IOptionValues,
    galleryAttributes: ReadonlyArray<IAttributePair>,
): boolean => {
    return galleryAttributes.every((v) => attributesInclude(attributes, v));
};

const getIdFromCurrentUrl = (initialVariantId: number): number => {
    const window = getWindow();
    if (!window) {
        return initialVariantId;
    }
    const currentUrl = window.location.href;
    const numberAtEnd = currentUrl.match(/\/v\/(\d+)\//);
    // Return the number or "initial variant id"
    return numberAtEnd ? Number(numberAtEnd[1]) : initialVariantId;
};

const findPlaceHolderGallery = (
    block: IGalleryBlock,
    initialVariantId: string | null | undefined,
): IGallery | null | undefined => {
    if (!initialVariantId) {
        return null;
    }
    const urlVariantId = getIdFromCurrentUrl(Number(initialVariantId));
    return block.galleries.find(
        (gallery) => Number(gallery.initial_variant) === urlVariantId,
    );
};

const findMatchingGallery = (
    block: IGalleryBlock,
    optionValues: IOptionValues,
    selectedRootProduct: IProduct | null,
    baseVariant: IProduct | null,
): IGallery | null => {
    // default galleries have no product or attribute set
    const defaultGallery = block.galleries.find((gallery) => {
        if (!gallery.product && !gallery.attributes?.length) {
            return true;
        }
        return false;
    });

    const matchtAttributesGallery = block.galleries.find((gallery) => {
        // only match on galleries with attributes selection and no product selection
        if (
            gallery.product || // don't match if product also selected
            !gallery.attributes?.length ||
            !selectedRootProduct ||
            !baseVariant
        ) {
            return false;
        }
        // all selected gallery variants match
        const variantMatch = attributesMatch(optionValues, gallery.attributes);
        return variantMatch;
    });

    const productMatchGallery = block.galleries.find((gallery) => {
        // only match on product selection but no attributes selection
        if (
            gallery.attributes?.length || // don't match if attributes also selected
            !gallery.product ||
            !selectedRootProduct ||
            !baseVariant
        ) {
            return false;
        }
        const isRootOrVariant =
            gallery.product === selectedRootProduct.id ||
            gallery.product === baseVariant.id;
        return isRootOrVariant;
    });

    const matchProductAndAttributesGallery = block.galleries.find((gallery) => {
        if (
            !gallery.attributes?.length || // attributes must be selected
            !gallery.product || // gallery must be selected
            !selectedRootProduct ||
            !baseVariant
        ) {
            return false;
        }
        const isRootOrVariant =
            gallery.product === selectedRootProduct.id ||
            gallery.product === baseVariant.id;
        // all selected gallery variants match
        const variantMatch = attributesMatch(optionValues, gallery.attributes);
        return isRootOrVariant && variantMatch;
    });

    if (matchProductAndAttributesGallery) {
        return matchProductAndAttributesGallery;
    } else if (productMatchGallery) {
        return productMatchGallery;
    } else if (matchtAttributesGallery) {
        return matchtAttributesGallery;
    } else if (defaultGallery) {
        return defaultGallery;
    }
    // reset to no gallery
    return null;
};

export const GalleryBlock = (props: IGalleryBlockProps): React.ReactNode => {
    const viewport: IViewport = useAppSelector(
        (state) => state.common.viewport,
    );
    const optionValues: IOptionValues = useAppSelector(
        (state) => state.configurator.ui.optionValues,
    );
    const selectedRootProduct: IProduct | null = useAppSelector((state) =>
        rootProductSelector(state.configurator),
    );
    const baseVariant: IProduct | null = useAppSelector((state) =>
        baseVariantSelector(state.configurator),
    );

    const block = decodeGalleryJSON(props.galleriesJSON);

    const placeHolder = findPlaceHolderGallery(block, props.initialVariantId);

    const gallery = findMatchingGallery(
        block,
        optionValues,
        selectedRootProduct,
        baseVariant,
    );

    React.useEffect(() => {
        void loadWistiaPlayer();
    }, []);

    if (!gallery) {
        // If gallery is not available, render GalleryPlaceHolder if placeHolder exists
        if (!placeHolder) {
            return null;
        }
        return (
            <GalleryPlaceHolder
                gallery={placeHolder}
                defaultThumbnail={props.defaultThumbnail}
                viewport={viewport}
            />
        );
    }

    return (
        <Gallery
            gallery={gallery}
            defaultThumbnail={props.defaultThumbnail}
            viewport={viewport}
        />
    );
};
